import { alpha, createTheme } from "@mui/material/styles";

export const buildTheme = (mode) => {
  const isLight = mode === "light";

  const palette = isLight
    ? {
        mode: "light",
        primary: {
          main: "#0D5C4D", // Emerald Green 800
          light: "#117B67", // Emerald Green 700
          dark: "#093F35", // Emerald Green 900
          contrastText: "#FFFFFF",
        },
        secondary: {
          main: "#475569", // Slate 600
          light: "#64748B", // Slate 500
          dark: "#1E293B", // Slate 800
          contrastText: "#FFFFFF",
        },
        background: {
          default: "#F8FAFC", // Slate 50 (clean grey slate background)
          paper: "#FFFFFF",
        },
        text: {
          primary: "#0F172A", // Slate 900
          secondary: "#475569", // Slate 600
        },
        divider: "rgba(148, 163, 184, 0.15)", // Slate 400 with opacity
      }
    : {
        mode: "dark",
        primary: {
          main: "#10B981", // Emerald Green 500
          light: "#34D399", // Emerald Green 400
          dark: "#059669", // Emerald Green 600
          contrastText: "#0B1E19", // Dark green-obsidian contrast
        },
        secondary: {
          main: "#94A3B8", // Slate 400 (Titanium slate)
          light: "#CBD5E1",
          dark: "#475569",
          contrastText: "#0F172A",
        },
        background: {
          default: "#0A0B10", // Midnight Slate-Black (professional, premium)
          paper: "#12131A", // Dark Charcoal Slate
        },
        text: {
          primary: "#F8FAFC", // Slate 50
          secondary: "#94A3B8", // Slate 400
        },
        divider: "rgba(148, 163, 184, 0.12)", // Slate-grey divider with low opacity
      };

  return createTheme({
    palette,
    shape: {
      borderRadius: 10, // Slightly more rounded for elegance
    },
    typography: {
      fontFamily:
        '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      h1: { fontWeight: 600 },
      h2: { fontWeight: 600 },
      h3: {
        fontWeight: 600,
        letterSpacing: "-0.02em",
      },
      h4: {
        fontWeight: 600,
        letterSpacing: "-0.02em",
      },
      h5: {
        fontWeight: 600,
        letterSpacing: "-0.01em",
      },
      h6: {
        fontWeight: 600,
        letterSpacing: "-0.01em",
      },
      subtitle1: { fontWeight: 500, letterSpacing: "-0.01em" },
      subtitle2: { fontWeight: 500, letterSpacing: "-0.01em" },
      body1: { fontWeight: 400 },
      body2: { fontWeight: 400 },
      button: { fontWeight: 600, letterSpacing: "0.02em" }, // A bit more spacing for buttons
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "*": {
            boxSizing: "border-box",
          },
          html: {
            backgroundColor: palette.background.paper,
            minWidth: 0,
            maxWidth: "100%",
          },
          body: {
            backgroundColor: palette.background.default,
            color: palette.text.primary,
            // Subtle warm gradient instead of the blue one
            backgroundImage: isLight
              ? "radial-gradient(circle at top center, rgba(148, 163, 184, 0.05), transparent 40%)"
              : "radial-gradient(circle at top center, rgba(16, 185, 129, 0.03), transparent 45%)",
            maxWidth: "100%",
          },
          "#root": {
            backgroundColor: palette.background.default,
            minWidth: 0,
            "@media (min-width:900px)": {
              width: "100%",
              maxWidth: "none",
              borderInline: 0,
              textAlign: "left",
            },
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            minWidth: 0,
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            width: "100%",
            maxWidth: "100%",
            overflowX: "auto",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 8,
            boxShadow: "none",
            "&:hover": {
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              transform: "translateY(-1px)",
            },
            transition: "all 0.2s ease-in-out",
          },
          containedPrimary: {
            // Elegant green gradient
            background: isLight
              ? "linear-gradient(135deg, #0D5C4D 0%, #093F35 100%)"
              : "linear-gradient(135deg, #10B981 0%, #34D399 100%)",
            color: isLight ? "#FFFFFF" : "#0B1E19", // Force readable text
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          label: {
            fontWeight: 600,
            letterSpacing: "-0.01em",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            backgroundImage: "none",
            boxShadow: isLight
              ? "0 8px 32px -8px rgba(12, 12, 11, 0.08)"
              : "0 8px 32px -8px rgba(0, 0, 0, 0.6)",
            transition:
              "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
            border: `1px solid ${isLight ? "rgba(148, 163, 184, 0.15)" : "rgba(148, 163, 184, 0.12)"}`,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: isLight
              ? alpha("#FFFFFF", 0.8)
              : alpha("#12131A", 0.6),
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: alpha(isLight ? "#0D5C4D" : "#10B981", 0.5),
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderWidth: "1px",
              borderColor: isLight ? "#0D5C4D" : "#10B981",
            },
          },
          notchedOutline: {
            borderColor: isLight
              ? "rgba(148, 163, 184, 0.25)"
              : "rgba(148, 163, 184, 0.18)",
          },
        },
      },
    },
  });
};
